/**
 * One brand colour → the eight palette steps the tokens read. Hue comes from the input; lightness
 * is fixed per step, so text contrast does not depend on the colour a company picks. Chroma comes
 * from the input, lowered on the light steps and reduced until the colour fits sRGB.
 */
export type BrandStep = 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900

export interface BrandScale {
  steps: Record<BrandStep, string>
  accent: string
}

export const BRAND_LIGHTNESS: Record<BrandStep, number> = {
  200: 0.86,
  300: 0.76,
  400: 0.64,
  500: 0.55,
  600: 0.47,
  700: 0.4,
  800: 0.33,
  900: 0.27,
}

const CHROMA_FACTOR: Partial<Record<BrandStep, number>> = {
  200: 0.4,
  300: 0.65,
  400: 0.85,
}

const ACCENT_HUE_TURN = Math.PI / 6

const HEX = /^#[0-9a-f]{6}$/i

type Rgb = [number, number, number]

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
const toGamma = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055)

function hexToRgb(hex: string): Rgb {
  const n = parseInt(hex.slice(1), 16)
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
}

function rgbToHex(rgb: Rgb): string {
  return `#${rgb
    .map((v) =>
      Math.round(Math.min(1, Math.max(0, v)) * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`
}

function linearToOklab([r, g, b]: Rgb): Rgb {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ]
}

function oklabToLinear([L, a, b]: Rgb): Rgb {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ]
}

const inGamut = (rgb: Rgb) => rgb.every((v) => v >= -1e-4 && v <= 1 + 1e-4)

export function oklchOf(hex: string): { l: number; c: number; h: number } | null {
  if (!HEX.test(hex)) return null
  const [l, a, b] = linearToOklab(hexToRgb(hex).map(toLinear) as Rgb)
  return { l, c: Math.hypot(a, b), h: Math.atan2(b, a) }
}

function fromLch(l: number, c: number, h: number): string {
  const at = (chroma: number) => oklabToLinear([l, chroma * Math.cos(h), chroma * Math.sin(h)])
  let low = 0
  let high = c
  if (inGamut(at(c))) {
    low = c
  } else {
    for (let i = 0; i < 24; i++) {
      const mid = (low + high) / 2
      if (inGamut(at(mid))) low = mid
      else high = mid
    }
  }
  return rgbToHex(at(low).map(toGamma) as Rgb)
}

export function brandScale(hex: string): BrandScale | null {
  const input = oklchOf(hex)
  if (!input) return null

  const steps = {} as Record<BrandStep, string>
  for (const [key, lightness] of Object.entries(BRAND_LIGHTNESS)) {
    const step = Number(key) as BrandStep
    steps[step] = fromLch(lightness, input.c * (CHROMA_FACTOR[step] ?? 1), input.h)
  }
  const accent = fromLch(
    BRAND_LIGHTNESS[300],
    input.c * (CHROMA_FACTOR[300] ?? 1),
    input.h + ACCENT_HUE_TURN,
  )
  return { steps, accent }
}

/** WCAG contrast of white text on this background. */
export function contrastOnWhiteText(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(toLinear)
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b
  return 1.05 / (luminance + 0.05)
}
